import { useState } from 'react';
import Skeleton from 'react-loading-skeleton';

import { Button } from '../../../../../cdl/Button/Button';
import { DropdownMenu } from '../../../../../cdl/DropdownMenu/DropdownMenu';
import { IconButton } from '../../../../../cdl/IconButton/IconButton';
import { Text } from '../../../../../cdl/Text/Text';
import { GenericError } from '../../../../../common/Error/GenericError';
import { translate } from '../../../../../common/helpers/translate.helper';
import { useOpenFreshchat } from '../../../../../common/hooks/useOpenFreshchat';
import { useRole } from '../../../../../common/hooks/useRole';
import { IconDots } from '../../../../../common/icons/cdl/Dots';
import { IconPlus } from '../../../../../common/icons/cdl/Plus';
import { Box } from '../../../../../common/ui/Box';
import { SsoAdvertisementBanner } from '../../../../components/SsoAdvertisementBanner';
import { SsoEmptyStateBanner } from '../../../../components/SsoEmptyStateBanner';
import { SsoHeader } from '../../../../components/SsoHeader';
import { useCustomerGroupDetails } from '../../hooks/useCustomerGroupDetails';
import { useCustomerGroupSettings } from '../../hooks/useCustomerGroupSettings';

import { CustomerAddConnectionIdModal } from './CustomerAddConnectionIdModal';
import { CustomerCreateSsoGroupModal } from './CustomerCreateSsoGroupModal';
import { CustomerDisableSsoModal } from './CustomerDisableSsoModal';
import { CustomerSsoGroupsTable } from './CustomerSsoGroupsTable';

interface CustomerSsoSettingsProps {
    groupId: string;
}

export const CustomerSsoSettings = ({ groupId }: CustomerSsoSettingsProps) => {
    const settingsQuery = useCustomerGroupSettings(groupId);
    const groupDetailsQuery = useCustomerGroupDetails(groupId);
    const role = useRole();
    const openFreshchat = useOpenFreshchat();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
    const [isDisableModalOpen, setIsDisableModalOpen] = useState(false);

    if (settingsQuery.isPending) {
        return <Skeleton height="200px" />;
    }

    if (settingsQuery.isError) {
        return <GenericError />;
    }

    const hasConnectionId = settingsQuery.data?.ssoConfiguration?.connectionId;
    const isSuperAdmin = role.isSuperAdmin();

    if (!hasConnectionId) {
        if (!isSuperAdmin) {
            return <SsoAdvertisementBanner onContactSupport={openFreshchat} />;
        }

        return (
            <>
                <SsoEmptyStateBanner onAddConnection={() => setIsModalOpen(true)} />

                <CustomerAddConnectionIdModal
                    isOpen={isModalOpen}
                    onDismiss={() => setIsModalOpen(false)}
                    groupId={groupId}
                    groupName={groupDetailsQuery.data?.name || ''}
                />
            </>
        );
    }

    const ssoConfiguration = settingsQuery.data.ssoConfiguration;

    if (!ssoConfiguration || !ssoConfiguration.connectionId) {
        return <GenericError />;
    }

    const hasGroups = Object.keys(ssoConfiguration.samlGroupMapping).length > 0;

    return (
        <Box display="flex" flexDirection="column" gap={24}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={24}>
                <SsoHeader connectionId={ssoConfiguration.connectionId} />

                {isSuperAdmin ? (
                    <Box display="flex" gap={8} alignItems="center">
                        <Button emphasis="high" LeadingIcon={IconPlus} onClick={() => setIsCreateGroupModalOpen(true)}>
                            {translate('sso.createGroup')}
                        </Button>
                        <DropdownMenu
                            align="end"
                            trigger={<IconButton Icon={IconDots} emphasis="medium" />}
                            options={[
                                {
                                    label: translate('sso.disableSso'),
                                    destructive: true,
                                    onSelect: () => setIsDisableModalOpen(true),
                                },
                            ]}
                        />
                    </Box>
                ) : null}
            </Box>

            {!hasGroups ? (
                <Text variant="small">{translate('sso.emptyTable')}</Text>
            ) : (
                <CustomerSsoGroupsTable
                    samlGroupMapping={ssoConfiguration.samlGroupMapping}
                    groupId={groupId}
                    readOnly={!isSuperAdmin}
                />
            )}

            {isSuperAdmin ? (
                <>
                    <CustomerCreateSsoGroupModal
                        isOpen={isCreateGroupModalOpen}
                        onDismiss={() => setIsCreateGroupModalOpen(false)}
                        groupId={groupId}
                    />
                    <CustomerDisableSsoModal
                        isOpen={isDisableModalOpen}
                        onDismiss={() => setIsDisableModalOpen(false)}
                        groupId={groupId}
                        groupName={groupDetailsQuery.data?.name || ''}
                        connectionId={ssoConfiguration.connectionId || ''}
                    />
                </>
            ) : null}
        </Box>
    );
};
